import { lazy, Suspense, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  coreExtensionData,
  createFrontendModule,
  Progress,
} from '@backstage/frontend-plugin-api';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { CreatePage } from './CreatePage';
import type { CreateSubPage } from './subPages';
import { TemplateCards } from './TemplateCards';
import { useTemplateWizard } from './useTemplateWizard';

/**
 * The generic creation wizard, loaded the first time it is opened (with the
 * form's validator and the generic fields): not on the template list's path.
 */
const GenericProjectWizard = lazy(async () => {
  const module = await import('../genericProjectWizard');
  return { default: module.GenericProjectWizard };
});

/**
 * « Créer » (`page:scaffolder`), kept as the plugin builds it — its id, its
 * path, route ref, title and icon (the original factory's outputs), its
 * `pages` and `formFields` inputs — with only its element replaced: the
 * PageBlueprint's layout (plugin header, one tab per sub-page) becomes
 * `CreatePage`, which routes the same attached sub-pages under its own
 * header and admin-only tabs.
 *
 * The form fields are untouched by this: the `formFields` input stays
 * declared on the same extension id, which is where the scaffolder's
 * form-fields API looks for page-attached fields
 * (`getPageFormFieldLoaders`: the `page:scaffolder` node's `formFields`
 * attachments); the app's own fields (FormFieldBlueprint) attach to
 * `api:scaffolder/form-fields`, which this module does not touch.
 */
const createPage = scaffolderPlugin.getExtension('page:scaffolder').override({
  factory(originalFactory, { inputs }) {
    const original = originalFactory();
    const pages: CreateSubPage[] = inputs.pages.map(page => ({
      path: page.get(coreExtensionData.routePath),
      title: page.get(coreExtensionData.title),
      element: page.get(coreExtensionData.reactElement),
    }));
    return [
      ...Array.from(original).filter(
        value => value.id !== coreExtensionData.reactElement.id,
      ),
      coreExtensionData.reactElement(<CreatePage pages={pages} />),
    ];
  },
});

/**
 * The templates sub-page's content: its list (`/create/templates`) is ours,
 * `TemplateCards`; a template's form (`/create/templates/:namespace/:name`)
 * is the wizard `useTemplateWizard` chooses —
 *  - a module's own wizard (`createWizard`, components/appModules) for its
 *    template — `odoo-project`'s, `OdooProjectWizard` (plugins/odoo) —,
 *    loaded with its form fields the first time it is opened;
 *  - the generic wizard (`GenericProjectWizard`) for a template annotated
 *    `waadoo.ovh/wizard: generic` whose type has no wizard of its own;
 *  - otherwise the original element untouched — upstream's wizard with the
 *    form fields it loads, the app's custom fields among them.
 * A progress bar while the annotation and the types are read, so that
 * upstream's form does not flash before the generic wizard replaces it.
 *
 * The original element (lazy, in its own ExtensionBoundary) routes
 * `:namespace/:templateName` itself, relative to the page's `templates/*`
 * route: it is rendered here outside any nested `<Route>` of ours, as the
 * tasks override does (components/taskProjectBanner/plugin.tsx).
 */
function TemplatesSubPage(props: { original: ReactNode }) {
  const rest = useParams()['*'] ?? '';
  const wizard = useTemplateWizard(rest === '' ? undefined : rest);
  if (rest === '') {
    return <TemplateCards />;
  }
  switch (wizard.kind) {
    case 'pending':
      return <Progress />;
    case 'module':
      return (
        <Suspense fallback={<Progress />}>
          <wizard.wizard.Wizard />
        </Suspense>
      );
    case 'generic':
      return (
        <Suspense fallback={<Progress />}>
          <GenericProjectWizard templateRef={wizard.templateRef} />
        </Suspense>
      );
    default:
      return <>{props.original}</>;
  }
}

/**
 * The scaffolder's « Templates » sub-page (`sub-page:scaffolder/templates`),
 * kept as the plugin builds it — path, title, config, the loader that reads
 * the form fields — with only its element wrapped (`TemplatesSubPage`).
 */
const templatesSubPage = scaffolderPlugin
  .getExtension('sub-page:scaffolder/templates')
  .override({
    factory(originalFactory) {
      const original = originalFactory();
      const element = original.get(coreExtensionData.reactElement);
      return [
        ...Array.from(original).filter(
          value => value.id !== coreExtensionData.reactElement.id,
        ),
        coreExtensionData.reactElement(<TemplatesSubPage original={element} />),
      ];
    },
  });

/**
 * On the `scaffolder` plugin: an extension of a module replaces the plugin's
 * extension with the same id, in place. Registered in
 * packages/app/src/App.tsx.
 */
export const createPageModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [createPage, templatesSubPage],
});
